import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { ArrowLeft, CalendarDays, Clock, Users, MapPin, Loader2, Coins, CheckCircle, XCircle, User, Ban, Award, CreditCard, Shield, Star, ReceiptText } from "lucide-react";
import { Input } from "@/components/ui/input";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

const statusStyle = { open: "bg-green-100 text-green-800", confirmed: "bg-blue-100 text-blue-800", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-800" };
const checkInStyle = { registered: "bg-blue-100 text-blue-800", checked_in: "bg-green-100 text-green-800", no_show: "bg-red-100 text-red-800", cancelled: "bg-slate-100 text-slate-600" };
const payStyle = { paid: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800" };

export default function SessionDetail() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(false);
  const [ratingScore, setRatingScore] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/coach/sessions/${sessionId}`);
      setSession(res.data);
    } catch { toast.error("Session not found"); navigate(-1); }
    setLoading(false);
  }, [sessionId, navigate]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    const stripeSessionId = searchParams.get("session_id");
    const paymentParam = searchParams.get("payment");
    if (stripeSessionId && paymentParam === "success") {
      setPaymentChecking(true);
      const poll = async (attempts = 0) => {
        if (attempts >= 6) { setPaymentChecking(false); return; }
        try {
          const res = await api.get(`/sessions/${sessionId}/payment-status`, { params: { stripe_session_id: stripeSessionId } });
          if (res.data.payment_status === "paid") {
            toast.success("Payment confirmed! You're in.");
            setPaymentChecking(false);
            fetchSession();
            return;
          }
        } catch {}
        setTimeout(() => poll(attempts + 1), 2000);
      };
      poll();
    }
  }, [searchParams, sessionId, fetchSession]);

  const isCoachHost = user?.user_id === session?.host_user_id;
  const myParticipation = (session?.participants || [])
    .filter(p => p.user_id === user?.user_id && p.status !== "cancelled")
    .sort((a, b) => {
      if (a.payment_status === "paid" && b.payment_status !== "paid") return -1;
      if (a.payment_status !== "paid" && b.payment_status === "paid") return 1;
      return (b.joined_at || "").localeCompare(a.joined_at || "");
    })[0];
  const isParticipant = !!myParticipation;
  const needsPayment = isParticipant && myParticipation?.payment_status === "pending" && session?.price_amount > 0;

  const handleJoin = async () => {
    if (!user) { navigate("/login"); return; }
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setJoining(true);
    try {
      const res = await api.post(`/sessions/${sessionId}/join`);
      if (res.data.needs_payment) {
        // Redirect to Stripe checkout
        const originUrl = window.location.origin;
        const checkout = await api.post(`/sessions/${sessionId}/checkout`, { session_id: sessionId, origin_url: originUrl });
        if (checkout.data.url) { window.location.href = checkout.data.url; return; }
      }
      toast.success("Joined session!");
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to join"); }
    setJoining(false);
  };

  const handlePayNow = async () => {
    if (user && !hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setJoining(true);
    try {
      const originUrl = window.location.origin;
      const checkout = await api.post(`/sessions/${sessionId}/checkout`, { session_id: sessionId, origin_url: originUrl });
      if (checkout.data.url) { window.location.href = checkout.data.url; return; }
    } catch (err) { toast.error(err.response?.data?.detail || "Payment failed"); }
    setJoining(false);
  };

  const handleLeave = async () => {
    try {
      await api.post(`/sessions/${sessionId}/leave`);
      toast.success("Left session");
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const updateParticipant = async (participantId, field, value) => {
    try {
      await api.put(`/coach/sessions/${sessionId}/participants/${participantId}`, { [field]: value });
      toast.success("Updated");
      fetchSession();
    } catch { toast.error("Failed to update"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!session) return null;

  const activeParticipants = session.participants?.filter(p => p.status !== "cancelled") || [];
  const spotsLeft = Math.max(0, session.slot_limit - activeParticipants.length);
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-6" data-testid="session-detail-page">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />Back
      </button>

      {/* Header */}
      <div className="bg-white border-2 border-slate-100 p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight" data-testid="session-title">{session.title}</h1>
              <Badge className={`${statusStyle[session.status]} text-[10px] font-heading uppercase`}>{session.status}</Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Award className="w-4 h-4 text-court-orange" /><span>Coach {session.host_name}</span>
            </div>
            {session.description && <p className="text-muted-foreground mt-2">{session.description}</p>}
          </div>
          <div className="text-right">
            <p className="font-heading text-3xl font-bold text-court-orange">₱{session.price_amount}</p>
            <p className="text-[10px] text-muted-foreground">{session.pricing_model === "per_player" ? "per player" : "per session"}</p>
          </div>
        </div>

        <Separator className="my-4" />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-court-orange" />{session.session_date}</div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-court-orange" />{session.start_time} ({session.duration_minutes}min)</div>
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-court-orange" />{activeParticipants.length}/{session.slot_limit} players</div>
          {session.venue_name && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-court-orange" />{session.venue_name}</div>}
        </div>
      </div>

      {/* Payment Checking Banner */}
      {paymentChecking && (
        <div className="bg-amber-50 border-2 border-amber-200 p-4 mb-6 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
          <p className="text-sm font-medium text-amber-800">Confirming your payment...</p>
        </div>
      )}

      {!isCoachHost && isParticipant && myParticipation?.payment_status === "paid" && myParticipation?.amount > 0 && (
        <div className="mb-6 flex justify-start">
          <Button variant="outline" onClick={() => navigate(`/receipts/session/${myParticipation.participant_id}`)} className="rounded-full border-2" data-testid="session-receipt-banner-btn">
            <ReceiptText className="w-4 h-4 mr-2" />View Receipt
          </Button>
        </div>
      )}

      {/* Player Action */}
      {!isCoachHost && session.status === "open" && (
        <div className="mb-6">
          {kycBlocked && <div className="mb-4"><KycBookingNotice message={kycMessage} compact testId="session-kyc-notice" /></div>}
          {isParticipant ? (
            <div className="flex items-center gap-3 p-4 border-2 border-slate-100">
              {needsPayment ? (
                <>
                  <CreditCard className="w-5 h-5 text-amber-500 shrink-0" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-700">Payment pending</p>
                    <p className="text-xs text-muted-foreground">Complete payment to confirm your spot</p>
                  </div>
                  <Button data-testid="pay-now-btn" size="sm" onClick={handlePayNow} disabled={joining || kycBlocked}
                    className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase text-xs px-5 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                    {joining ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CreditCard className="w-3 h-3 mr-1" />}
                    Pay ₱{session.price_amount}
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleLeave} className="rounded-full border-2 border-red-200 text-red-500 text-xs">Leave</Button>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                  <p className="font-medium text-green-700 flex-1">You're confirmed for this session!</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground"><Shield className="w-3 h-3" />Paid</div>
                  {myParticipation?.amount > 0 && (
                    <Button size="sm" variant="outline" onClick={() => navigate(`/receipts/session/${myParticipation.participant_id}`)} className="rounded-full border-2 text-xs" data-testid="session-receipt-btn">
                      <ReceiptText className="w-3 h-3 mr-1" />Receipt
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={handleLeave} className="rounded-full border-2 border-red-200 text-red-500 text-xs">Leave</Button>
                </>
              )}
            </div>
          ) : spotsLeft > 0 ? (
            <Button data-testid="join-session-btn" onClick={handleJoin} disabled={joining || kycBlocked}
              className="w-full md:w-auto bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase px-10 h-12 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
              {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> :
               kycBlocked ? <Shield className="w-4 h-4 mr-2" /> :
               session.price_amount > 0 ? <CreditCard className="w-4 h-4 mr-2" /> : <Users className="w-4 h-4 mr-2" />}
              {kycBlocked ? "KYC Approval Required" : session.price_amount > 0 ? `Join & Pay ₱${session.price_amount}` : "Join Session - Free"}
            </Button>
          ) : (
            <p className="text-center py-4 font-heading text-muted-foreground uppercase">Session is full</p>
          )}
        </div>
      )}

      {/* Participants (visible to coach) */}
      <div>
        <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-court-orange" />Participants ({activeParticipants.length})
        </h2>
        {activeParticipants.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed border-slate-200">
            <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
            <p className="font-heading text-muted-foreground uppercase text-sm">No participants yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeParticipants.map(p => (
              <div key={p.participant_id} className="bg-white border border-slate-100 p-3 flex items-center gap-3 text-sm" data-testid={`participant-${p.participant_id}`}>
                <User className="w-5 h-5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{p.user_name}</p>
                  <p className="text-[10px] text-muted-foreground">{p.user_email}</p>
                </div>
                <Badge className={`${checkInStyle[p.check_in_status]} text-[10px] font-heading uppercase`}>{p.check_in_status?.replace("_", " ")}</Badge>
                <Badge className={`${payStyle[p.payment_status]} text-[10px] font-heading uppercase`}>{p.payment_status}</Badge>
                {isCoachHost && session.status === "open" && (
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] rounded-full"
                      onClick={() => updateParticipant(p.participant_id, "check_in_status", "checked_in")}>
                      <CheckCircle className="w-3 h-3 text-green-500" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] rounded-full"
                      onClick={() => updateParticipant(p.participant_id, "check_in_status", "no_show")}>
                      <Ban className="w-3 h-3 text-red-500" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] rounded-full"
                      onClick={() => updateParticipant(p.participant_id, "payment_status", "paid")}>
                      <Coins className="w-3 h-3 text-amber-500" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rating Section (for participants after completion) */}
      {session.status === "completed" && !isCoachHost && isParticipant && !hasRated && (
        <div className="mt-8 bg-white border-2 border-amber-200/50 p-6" data-testid="rate-coach-section">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400" />Rate Coach {session.host_name}
          </h2>
          <div className="flex gap-1 mb-4">
            {[1, 2, 3, 4, 5].map(s => (
              <button key={s} data-testid={`star-${s}`} onClick={() => setRatingScore(s)}
                className="p-1 transition-transform hover:scale-110">
                <Star className={`w-8 h-8 ${s <= ratingScore ? 'text-amber-400 fill-amber-400' : 'text-slate-200 hover:text-amber-200'}`} />
              </button>
            ))}
          </div>
          <Input data-testid="rating-comment" value={ratingComment} onChange={e => setRatingComment(e.target.value)}
            placeholder="Share your experience (optional)" className="h-11 rounded-none border-2 mb-4" />
          <Button data-testid="submit-rating-btn" onClick={async () => {
            if (ratingScore === 0) { toast.error("Please select a rating"); return; }
            setRatingSubmitting(true);
            try {
              await api.post(`/sessions/${sessionId}/rate-coach`, { score: ratingScore, comment: ratingComment });
              toast.success("Rating submitted! Thank you.");
              setHasRated(true);
            } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit rating"); }
            setRatingSubmitting(false);
          }} disabled={ratingSubmitting || ratingScore === 0}
            className="bg-amber-400 hover:bg-amber-500 text-black rounded-full font-heading uppercase px-6 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
            {ratingSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Star className="w-4 h-4 mr-2" />}
            Submit Rating
          </Button>
        </div>
      )}
      {hasRated && (
        <div className="mt-8 bg-green-50 border border-green-200 p-4 text-center">
          <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-2" />
          <p className="font-heading text-sm uppercase text-green-700">Thank you for your rating!</p>
        </div>
      )}
    </div>
  );
}
